import i18n from '../i18n';
import { resolveLocaleBase } from './documentLocale';

/**
 * ── THE SYSTEM DATE/TIME FORMAT (owner decision 2026-09-17) ──────────────────────────────────────
 *
 * Every date the UI shows or accepts is `dd-mm-yyyy`, every time is 24-hour `HH:mm` — regardless of
 * the viewer's machine, OS region or browser language. `toLocaleDateString()`, `toLocaleTimeString()`,
 * `toLocaleString()`, `Intl.DateTimeFormat(undefined, …)` and native `<input type="date|time|month">`
 * all render per MACHINE and are banned for display; use these helpers and the fields in
 * `components/dateFields.tsx` instead.
 *
 * These helpers only FORMAT. They never move a value between time zones: pass the `Date` your code
 * already builds (e.g. `parseApiUtc(v)` for a UTC instant, a wall-clock parse for a kickoff) and the
 * output uses that Date's local fields, exactly as `toLocaleDateString()` did. The single exception is a
 * DATE-ONLY string (`2026-09-17` / `2026-09-17T00:00:00`), which is a calendar day and is read as such so
 * it can never roll to the previous day west of UTC.
 *
 * Month and weekday NAMES follow the app language (en/ar/fr) — never the machine — with Latin digits.
 */

export type DateInput = Date | string | number | null | undefined;

const pad2 = (n: number) => String(n).padStart(2, '0');

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00(?::00(?:\.0+)?)?)?$/;

/** Turn an input into a Date without shifting zones. Invalid/empty → null. */
export const toDate = (value: DateInput): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string') {
    const m = DATE_ONLY_RE.exec(value.trim());
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** `17-09-2026` */
export const appDate = (value: DateInput): string => {
  const d = toDate(value);
  return d ? `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}` : '';
};

/** `14:05` (24-hour). Pass `withSeconds` for `14:05:09`. */
export const appTime = (value: DateInput, withSeconds = false): string => {
  const d = toDate(value);
  if (!d) return '';
  const base = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  return withSeconds ? `${base}:${pad2(d.getSeconds())}` : base;
};

/** `17-09-2026 14:05` */
export const appDateTime = (value: DateInput, withSeconds = false): string => {
  const d = toDate(value);
  return d ? `${appDate(d)} ${appTime(d, withSeconds)}` : '';
};

/** `17-09` — for compact day+month labels (chart axes, calendar chips). */
export const appDayMonth = (value: DateInput): string => {
  const d = toDate(value);
  return d ? `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}` : '';
};

/** `09-2026` — month pickers, pay periods. */
export const appMonthYear = (value: DateInput): string => {
  const d = toDate(value);
  return d ? `${pad2(d.getMonth() + 1)}-${d.getFullYear()}` : '';
};

/** Intl locale for NAMES only: the app language, Latin digits, never the machine. */
// `ca-gregory`: some runtimes default Arabic to the Islamic calendar's month names.
export const appNameLocale = (): string => `${resolveLocaleBase(i18n.language)}-u-ca-gregory-nu-latn`;

/** `Thursday` / `Thu` in the app language. */
export const appWeekday = (value: DateInput, style: 'long' | 'short' | 'narrow' = 'long'): string => {
  const d = toDate(value);
  return d ? new Intl.DateTimeFormat(appNameLocale(), { weekday: style }).format(d) : '';
};

/** `September` / `Sep` in the app language. */
export const appMonthName = (value: DateInput, style: 'long' | 'short' = 'long'): string => {
  const d = toDate(value);
  return d ? new Intl.DateTimeFormat(appNameLocale(), { month: style }).format(d) : '';
};

/** `September 2026` / `Sep 2026` in the app language — only where a month NAME is the design. */
export const appMonthNameYear = (value: DateInput, style: 'long' | 'short' = 'long'): string => {
  const d = toDate(value);
  return d ? `${appMonthName(d, style)} ${d.getFullYear()}` : '';
};

// ── Wire <-> display for typed fields ────────────────────────────────────────────────────────────

/** `YYYY-MM-DD` (API/wire) → `DD-MM-YYYY` (display). Anything else → ''. */
export const isoDateToDisplay = (iso?: string | null): string => {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
};

/**
 * Strict `DD-MM-YYYY` (also accepts `/` or `.` separators) → `YYYY-MM-DD`, or null when it is not a
 * real calendar day (31-02-2026 is rejected, not rolled into March).
 */
export const displayDateToIso = (text?: string | null): string | null => {
  const m = text ? /^\s*(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})\s*$/.exec(text) : null;
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return `${year}-${pad2(month)}-${pad2(day)}`;
};

/** Strict 24-hour `H:mm`/`HH:mm` → `HH:mm`, or null. */
export const normalizeTime = (text?: string | null): string | null => {
  const m = text ? /^\s*(\d{1,2}):(\d{2})\s*$/.exec(text) : null;
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h <= 23 && min <= 59 ? `${pad2(h)}:${pad2(min)}` : null;
};

/** `YYYY-MM` → `MM-YYYY`. */
export const isoMonthToDisplay = (iso?: string | null): string => {
  const m = iso ? /^(\d{4})-(\d{2})/.exec(iso) : null;
  return m ? `${m[2]}-${m[1]}` : '';
};

/** Strict `MM-YYYY` → `YYYY-MM`, or null. */
export const displayMonthToIso = (text?: string | null): string | null => {
  const m = text ? /^\s*(\d{1,2})[-/.](\d{4})\s*$/.exec(text) : null;
  if (!m) return null;
  const month = Number(m[1]);
  return month >= 1 && month <= 12 ? `${m[2]}-${pad2(month)}` : null;
};
